import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Users, ArrowLeft, Search, LogIn, Check, X } from "lucide-react";
import { useLang } from "@/lib/lang";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import NavBar from "@/components/NavBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GamesFriends() {
  const { lang } = useLang();
  const { user, login } = useAuth();
  const [target, setTarget] = useState("");
  const [rows, setRows] = useState([]);
  const T = lang === "fr" ? FR : EN;

  const load = () => user && axios.get(`${API}/games/friends/list`, { withCredentials: true }).then(r => setRows(r.data.friends));
  useEffect(() => { load(); }, [user]);

  const send = async () => {
    if (!user) { toast.error(T.loginFirst); return; }
    if (!target.trim()) return;
    try {
      await axios.post(`${API}/games/friends/request`, { target_username: target.trim() }, { withCredentials: true });
      toast.success(T.sent); setTarget(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || T.error); }
  };

  const accept = async (id) => {
    try {
      await axios.post(`${API}/games/friends/accept/${id}`, {}, { withCredentials: true });
      toast.success(T.accepted); load();
    } catch (e) { toast.error(e?.response?.data?.detail || T.error); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="games-friends-page">
      <NavBar />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/games" className="text-sm text-zinc-500 hover:text-[#8b6f3d] flex items-center gap-1"><ArrowLeft size={14} /> {T.back}</Link>
        <h1 className="font-serif-mystery text-4xl mt-3 mb-6">👥 {T.title}</h1>
        <p className="text-zinc-600 mb-6">{T.intro}</p>

        {!user && (
          <Button onClick={login} className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-11 px-5 mb-4">
            <LogIn size={16} className="mr-2" /> {T.loginToUse}
          </Button>
        )}

        {user && (
          <>
            <div className="flex gap-2 mb-6">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input data-testid="friend-target-input" value={target} onChange={e => setTarget(e.target.value)}
                  placeholder={T.searchPlaceholder} className="w-full border border-zinc-300 rounded-full h-11 pl-9 pr-4" />
              </div>
              <Button data-testid="friend-send-btn" onClick={send} className="bg-[#8b6f3d] text-white rounded-full h-11 px-5">{T.send}</Button>
            </div>

            <h2 className="font-serif-mystery text-xl mb-3">{T.yourFriends}</h2>
            <div className="space-y-2" data-testid="friends-list">
              {rows.length === 0 && <p className="text-center text-zinc-500 py-4">{T.noFriends}</p>}
              {rows.map(r => (
                <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl border border-zinc-200" data-testid={`friend-${r.id}`}>
                  <Users size={16} className="text-[#8b6f3d]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {r.from_id === user.user_id ? r.to_username : r.from_username}
                    </div>
                    <div className="text-xs text-zinc-500">{r.status === "pending" ? (r.to_id === user.user_id ? T.receivedRequest : T.pending) : T.accepted}</div>
                  </div>
                  {r.status === "pending" && r.to_id === user.user_id && (
                    <Button size="sm" data-testid={`accept-${r.id}`} onClick={() => accept(r.id)} className="bg-emerald-600 text-white rounded-full h-8 px-3">
                      <Check size={14} className="mr-1" /> {T.accept}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
const FR = { back: "Retour aux jeux", title: "Amis", intro: "Ajoute tes amis grâce à leur identifiant. La distance n'a aucune importance — jouez ensemble depuis n'importe où sur la planète.", searchPlaceholder: "Nom d'utilisateur", send: "Envoyer", loginFirst: "Connecte-toi d'abord.", loginToUse: "Connecte-toi pour gérer tes amis", sent: "Demande envoyée !", accepted: "Demande acceptée !", error: "Erreur.", yourFriends: "Tes amis et demandes", noFriends: "Aucun ami pour le moment.", receivedRequest: "Demande reçue", pending: "En attente", accept: "Accepter" };
const EN = { back: "Back to games", title: "Friends", intro: "Add friends by their username. Distance doesn't matter — play together from anywhere.", searchPlaceholder: "Username", send: "Send", loginFirst: "Sign in first.", loginToUse: "Sign in to manage friends", sent: "Request sent!", accepted: "Request accepted!", error: "Error.", yourFriends: "Your friends and requests", noFriends: "No friends yet.", receivedRequest: "Request received", pending: "Pending", accept: "Accept" };
